import logger from '../debug.js';
import { summarizePrivacy } from '../privacy.js';
import { getPageUrl } from '../renderEnv.js';
import { AdTagResponse } from './AdTagResponse.js';
import { SURFACE_CONTEXT, isPositiveSize, sizeFits } from '../surface/contract.js';

const log = logger.for('request');

/** Hard timeout over the entire request including server processing. */
const REQUEST_TIMEOUT_MS = 1000;
export const MAX_EXCLUDED_SOURCE_IDS = 32;

/**
 * Fetches an ad from the adtag endpoint.
 *
 * Enforces a hard 1s timeout per attempt, retrying once on timeout or network
 * failure (never on a definitive server response). Returns an AdTagResponse
 * on success — including clean no-fill, which may come back as HTTP 204 with
 * no body or HTTP 200 with a JSON envelope.
 * Throws on network failure, timeout, or an unexpected server error status.
 *
 * @param {object} params
 * @param {string} params.placementId
 * @param {object} params.privacySnapshot - normalized `@neuronic/privacy` snapshot
 * @param {object} params.renderEnv  - captured render environment from getRenderEnv()
 * @param {object} params.surfaceCapabilities - resolved by SurfaceController
 * @param {object} [params.deviceSua] - bounded high-entropy User-Agent Client Hints
 * @param {boolean} [params.forceBid=false] - dev only: forces TestBidderTask to inject a
 *   synthetic winning bid. Activated by ?neuronic_force_bid=1 URL param at bootstrap.
 *   Requires the server to be running in a non-production environment.
 * @returns {Promise<AdTagResponse>}
 * @throws {Error} on timeout, network failure, or unexpected server error response
 */
export async function fetchAd({
  placementId,
  privacySnapshot,
  renderEnv,
  surfaceCapabilities,
  deviceSua,
  forceBid = false,
  endpointOverride = null,
  excludedSourceIds = [],
}) {
  const url     = `${endpointOverride ?? __ADTAG_ENDPOINT__}/publisher/adtag`;
  const payload = buildPayload({
    placementId,
    privacySnapshot,
    renderEnv,
    surfaceCapabilities,
    deviceSua,
    forceBid,
    excludedSourceIds,
  });
  const credentials = privacySnapshot.capabilities.trackCookie ? 'include' : 'omit';

  log.debug('fetching ad', {
    placementId,
    credentials,
    privacy: summarizePrivacy(privacySnapshot),
  });

  let response;
  try {
    response = await attemptFetch(url, payload, placementId, credentials);
  } catch (firstErr) {
    // One retry, only for timeout / failure-to-connect. A definitive server
    // response (non-2xx) is not retried. The warn log states which of the
    // two failure modes triggered the retry.
    log.warn(`${firstErr.message} - retrying once`, { placementId });
    response = await attemptFetch(url, payload, placementId, credentials);
  }

  if (!response.ok) {
    throw new Error(`Ad server error ${response.status} for placement ${placementId}`);
  }

  const raw = response.status === 204
    ? {}
    : await response.json();
  const result = new AdTagResponse(raw);

  log.debug('ad response received', { placementId, hasBid: result.hasBid() });

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Performs one POST attempt with its own fresh 1s abort budget.
 * Throws a classified error distinguishing the two retryable failure modes:
 * hard timeout (budget exceeded) vs network failure (could not connect).
 *
 * @param {string} url
 * @param {object} payload
 * @param {string} placementId
 * @returns {Promise<Response>}
 */
async function attemptFetch(url, payload, placementId, credentials) {
  const controller = new AbortController();
  const timeoutId  = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
      signal:  controller.signal,
      credentials,
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error(`Ad request timed out after ${REQUEST_TIMEOUT_MS}ms (placement: ${placementId})`);
    }
    throw new Error(`Ad request network error: ${err.message} (placement: ${placementId})`);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Assembles the wire payload from placement id, consent signals, and render env.
 * page_url is only included when a known-good URL can be confirmed.
 * force_bid is only included when true — omitted entirely in normal production requests.
 *
 * @param {object} params
 * @param {string} params.placementId
 * @param {object} params.consent
 * @param {object} params.renderEnv
 * @param {boolean} params.forceBid
 * @returns {object}
 */
function buildPayload({ placementId, privacySnapshot, renderEnv, surfaceCapabilities, deviceSua, forceBid, excludedSourceIds }) {
  if (!validSurfaceCapabilities(surfaceCapabilities))
    throw new Error('surfaceCapabilities must be resolved before requesting an ad');
  if (!Array.isArray(excludedSourceIds)
    || excludedSourceIds.length > MAX_EXCLUDED_SOURCE_IDS
    || excludedSourceIds.some(id => typeof id !== 'string' || id.trim().length === 0)
    || new Set(excludedSourceIds).size !== excludedSourceIds.length) {
    throw new Error('excludedSourceIds must be a bounded list of unique non-empty strings');
  }

  const payload = {
    placement_id: placementId,
    privacy:      privacySnapshot,
    // Temporary compatibility mirror for Exchange versions predating the
    // normalized snapshot. It is derived from the same source, not re-resolved.
    consent:      buildLegacyConsent(privacySnapshot),
    render_caps: surfaceCapabilities,
  };

  const pageUrl = getPageUrl(renderEnv);

  if (pageUrl)
    payload.page_url = pageUrl;

  if (forceBid)
    payload.force_bid = true;

  if (deviceSua)
    payload.device_sua = deviceSua;

  if (excludedSourceIds.length > 0)
    payload.excluded_source_ids = excludedSourceIds;

  return payload;
}

function validSurfaceCapabilities(caps) {
  return !!caps
    && Object.values(SURFACE_CONTEXT).includes(caps.context)
    && isPositiveSize(caps.current_size)
    && isPositiveSize(caps.maximum_size)
    && isPositiveSize(caps.overlay_size)
    && sizeFits(caps.maximum_size, caps.current_size)
    && [
      'supports_content_resize',
      'supports_viewport',
      'supports_push_expansion',
      'supports_overlay_expansion',
      'top_frame',
    ].every(field => typeof caps[field] === 'boolean');
}

/**
 * Maps resolved consent signals to the wire format expected by the server.
 * Absent signals are omitted entirely — the server treats absence as unavailable.
 *
 * @param {object} privacySnapshot - from `@neuronic/privacy`
 * @returns {object}
 */
function buildLegacyConsent(privacySnapshot) {
  const out = {};
  const { tcf, gpp } = privacySnapshot.signals;

  if (tcf.status !== 'unavailable') {
    out.tcf = {
      tc_string:    tcf.tcString ?? '',
      gdpr_applies: tcf.gdprApplies ?? null,
    };
  }

  if (gpp.status !== 'unavailable') {
    out.gpp = {
      gpp_string:              gpp.gppString ?? '',
      gpp_applicable_sections: gpp.applicableSections ?? [],
    };
  }

  return out;
}
